import Filter from "@/components/ui/filters";
import {
  Badge,
  Button,
  Icon,
  RowCard,
  useToast,
} from "@elaniintc/goes-design-system";
import TablePaginantion from "@/components/common/table/pagination";
import SortDropdown from "../procesos/components/SortDropdown";
import { useEffect, useState } from "react";
import {
  filterTypesEnum,
  sortDirectionEnum,
  type CurrentFiltersType,
  type FilterTypeList,
  type MultiEntitySelections,
  type SortDirectionType,
  type SortOptionsTypes,
} from "@/types/common/filters";
import SearchInput from "@/components/common/search-input";
import { parseAsString, useQueryState } from "nuqs";
import NoData from "@/components/ui/no-data";
import NewTableModal from "./components/NewTableModal";
import TableDropdown from "./components/TableDropdown";
import {
  useCreateTable,
  useGetTables,
  useReactivateTableById,
  useRemoveTableById,
  type Table,
} from "@/queries/data-center/data-center.api";
import { useDebounceValue } from "usehooks-ts";
import EditTableModal from "./components/EditTableModal";
import { PageTabs } from "@/components/ui/page-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { NavLink } from "react-router";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { cn } from "@/utils/theme";
import DeactivateTableModal from "./components/deactivate-modal";
import { formatDateToCommaString, formatToRelativeTime } from "@/utils/dates";
import { FunctionalCore } from "./functional-core";

type DataCenterTabType = "internal" | "core";
const dataCenterTabs = [
  {
    label: "Internos",
    key: "internal",
  },
  {
    label: "Core Funcional",
    key: "core",
  },
];

const CentroDeDatos = () => {
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions["data-center"].create);
  const canReadRegistry = hasPermission(
    Permissions["data-center"]["read-registry"],
  );

  const [isOpenTableModal, setIsOpenTableModal] = useState<boolean>(false);
  const [deactivateTableModal, setDeactivateTableModal] = useState<{
    isActive: boolean;
    tableId?: string;
    tableName?: string;
  }>({ isActive: false, tableId: undefined, tableName: undefined });
  const [isOpenEditTableModal, setIsOpenEditTableModal] =
    useState<boolean>(false);
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.asc,
  );

  const [page, setPage] = useState(1);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [selectedTab, setSelectedTab] = useState<DataCenterTabType>("internal");
  const [coreParam, setCoreParam] = useQueryState("core", parseAsString);
  const [searchInput, setSearchInput] = useState(query || "");
  const [orderField, setOrderField] = useState<string | undefined>("name");
  const [filters, setFilters] = useState<CurrentFiltersType[] | []>([
    { status: "ACTIVE" },
  ]);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const {
    data: dataTables,
    isLoading,
    isFetching,
    refetch,
  } = useGetTables({
    page,
    per_page: 20,
    order_by: orderField,
    orderDirection,
    ...(deferredQuery ? { search: deferredQuery } : {}),
    filters,
  });

  const { mutateAsync: createTable } = useCreateTable();
  const { mutateAsync: deactivateTable, isPending: isLoadingDeactivate } =
    useRemoveTableById();
  const { mutateAsync: reactivateTable } = useReactivateTableById();

  const handleapplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setPage(page);
    setOrderField(field);
    setOrderDirection(direction);
  };

  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const { showToast } = useToast();

  const sortFields: SortOptionsTypes[] = [
    { label: "Nombre", value: "name" },
    { label: "Estado", value: "trashed" },
    { label: "Fecha de actualización", value: "updated_at" },
    { label: "Fecha de creación", value: "created_at" },
  ];

  const FilterFields: FilterTypeList[] = [
    {
      label: "Fecha de creación",
      type: filterTypesEnum.dates,
      field: "created_at",
    },
    {
      label: "Última Actualización",
      type: filterTypesEnum.dates,
      field: "updated_at",
    },
    {
      label: "Estado",
      type: filterTypesEnum.status,
      field: "status",
      options: [
        { label: "Activos", field: "ACTIVE" },
        { label: "Inactivo", field: "INACTIVE" },
      ],
    },
  ];

  useEffect(() => {
    // select tab if core=true
    if (coreParam === "true") {
      setSelectedTab("core");
    }
  }, [coreParam]);

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  const onDeactivateTable = async (val: { reason: string }) => {
    try {
      if (deactivateTableModal.tableId) {
        await deactivateTable({
          id: deactivateTableModal.tableId,
          reason: val?.reason ?? "",
        });
        setDeactivateTableModal({
          isActive: false,
          tableId: undefined,
          tableName: undefined,
        });
        showToast({
          title: "Tabla desactivada",
          description:
            "La tabla ha sido desactivada correctamente. Puedes volver a activarla desde su configuración.",
          options: {
            duration: 5000,
            type: "error",
          },
        });
        refetch();
      }
    } catch {
      showToast({
        title: "Error al desactivar tabla",
        description: "La tabla no pudo ser desactivada.",
        options: {
          duration: 5000,
          type: "error",
        },
      });
    }
  };

  useEffect(() => {
    if (searchInput === "") {
      refetch();
    }
  }, [searchInput, refetch]);

  const NoDataToShow = () => {
    return (
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
        <NoData
          title=" Aún no existen tablas"
          description="Crea una tabla para poder editar sus detalles"
        >
          {canCreate && (
            <Button
              variant="default"
              size="sm"
              className="items-center gap-2 rounded-lg cursor-pointer leading-2xl"
              onClick={() => setIsOpenTableModal(true)}
            >
              Nueva tabla
              <Icon type="far" name="plus" className="text-current" />
            </Button>
          )}
        </NoData>
      </div>
    );
  };

  const isInitialNoData =
    !isLoading &&
    !isFetching &&
    (!dataTables?.data || dataTables?.data.length === 0) &&
    !searchInput &&
    !deferredQuery &&
    JSON.stringify(filters) === JSON.stringify([{ status: "ACTIVE" }]);

  return (
    <>
      <NewTableModal
        open={isOpenTableModal}
        onClose={() => setIsOpenTableModal(false)}
        onSuccess={() => setIsOpenTableModal(false)}
      />
      {selectedTable && (
        <EditTableModal
          tableData={selectedTable}
          open={isOpenEditTableModal}
          onSuccess={() => {
            setIsOpenEditTableModal(false);
            setSelectedTable(null);
            refetch();
          }}
          onClose={() => {
            setIsOpenEditTableModal(false);
            setSelectedTable(null);
          }}
        />
      )}
      {deactivateTableModal.isActive && (
        <DeactivateTableModal
          isOpen={deactivateTableModal.isActive}
          isLoading={isLoadingDeactivate}
          onAccept={onDeactivateTable}
          onClose={() => {
            setDeactivateTableModal({
              isActive: false,
              tableId: undefined,
              tableName: undefined,
            });
          }}
        />
      )}

      <PageTabs
        className="mx-2"
        activeKey={selectedTab}
        onTabChange={(tab) => {
          setSelectedTab(tab as DataCenterTabType);
          if (tab === "core") {
            setCoreParam("true");
          } else {
            setCoreParam(null);
          }
        }}
        tabs={dataCenterTabs}
      />
      {selectedTab === "internal" ? (
        isLoading ||
        (isFetching &&
          !searchInput &&
          JSON.stringify(filters) === JSON.stringify([{ status: "ACTIVE" }]) &&
          !dataTables?.data &&
          !deferredQuery) ? (
          <div
            className="w-full flex flex-col space-y-2 p-2 pt-8"
            data-testid="page-skeleton"
          >
            <Skeleton className="h-5 w-104.5" />
            <div className="flex justify-between pt-10">
              <div className="flex space-x-3">
                <Skeleton className="h-9 w-29.5" />
                <Skeleton className="h-9 w-19.75" />
              </div>
              <div className="flex space-x-3">
                <Skeleton className="h-9 w-59.5" />
                <Skeleton className="h-9 w-34.5" />
              </div>
            </div>
            <div className="w-full flex flex-col mt-2 gap-2">
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton
                  key={idx}
                  className="w-full gap-4 h-17 rounded-xl px-4 py-3"
                />
              ))}
            </div>
          </div>
        ) : isInitialNoData ? (
          <NoDataToShow />
        ) : (
          <div className="flex-1 px-2 pt-8 w-full">
            <div className="flex flex-col gap-8">
              <p className="font-normal text-body leading-3xl">
                Crea nuevas tablas para utilizar sus datos dentro de tus
                procesos.
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex justify-between">
                  <div className="flex space-x-3">
                    <SortDropdown
                      currentOrderField={orderField}
                      currentOrderDirection={orderDirection}
                      sortOptions={sortFields}
                      onClick={handleapplySort}
                    />
                    <Filter
                      filters={FilterFields}
                      currentFilters={filters}
                      onCurrentFiltersChange={(e: CurrentFiltersType[]) => {
                        setFilters(e);
                        setPage(1);
                      }}
                      selecteds={selectedEntities}
                      onSetSelecteds={setSelectedEntities}
                    />
                  </div>
                  <div className="flex flex-column gap-2">
                    <SearchInput
                      data-testid="search-input-centro-datos"
                      variant="xs"
                      placeholder="Buscar tablas..."
                      className="h-9 w-60 text-sm"
                      value={searchInput || ""}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                    {canCreate && (
                      <Button
                        variant="default"
                        size="sm"
                        className="items-center gap-2 cursor-pointer py-1.5 px-2"
                        onClick={() => setIsOpenTableModal(true)}
                      >
                        Nueva tabla
                        <Icon type="far" name="plus" className="text-current" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="w-full flex flex-col gap-2">
                  {!dataTables?.data?.length ? (
                    <div className="w-full flex justify-center items-center min-h-125">
                      <h2 className="text-heading-quiet font-semibold text-2xl">
                        Sin resultados
                      </h2>
                    </div>
                  ) : (
                    <div className="w-full flex flex-col gap-2 overflow-y-hidden">
                      <div className="flex flex-col gap-2 flex-auto overflow-y-auto">
                        {dataTables?.data?.map((table, idx) => {
                          return (
                            <NavLink
                              key={table.id}
                              to={`/centro-de-datos/${table.id}`}
                              onClick={(e) => {
                                if (!canReadRegistry) {
                                  e.preventDefault();
                                }
                              }}
                              className={cn(
                                !canReadRegistry && "cursor-default",
                              )}
                            >
                              <RowCard
                                key={table.description + idx}
                                title={
                                  table?.table_display_name ||
                                  table?.name ||
                                  "Nombre de tabla"
                                }
                                subtitle={`Última actualización ${formatToRelativeTime(table.updated_at)}  |  Creado el ${formatDateToCommaString(table.created_at)}`}
                                icon={{ name: "table" }}
                              >
                                <RowCard.Right>
                                  <Badge
                                    size="sm"
                                    className="rounded-lg"
                                    shouldHover={false}
                                    variant={
                                      table.deleted_at ? "neutral" : "success"
                                    }
                                  >
                                    {table.deleted_at ? "Inactivo" : "Activo"}
                                  </Badge>
                                  <TableDropdown
                                    id={table.id}
                                    isInactive={!!table.deleted_at}
                                    onEdit={() => {
                                      setSelectedTable(table);
                                      setIsOpenEditTableModal(true);
                                    }}
                                    onDuplicate={async () => {
                                      setSelectedTable(table);
                                      await createTable({
                                        name: table.name,
                                        description: table.description,
                                        table_display_name:
                                          table.table_display_name,
                                      });
                                      await refetch();
                                    }}
                                    onDeactivate={async (id) => {
                                      setDeactivateTableModal({
                                        isActive: true,
                                        tableId: id,
                                        tableName: table.name,
                                      });
                                    }}
                                    onReactivate={async (id) => {
                                      await reactivateTable(id);
                                    }}
                                  />
                                </RowCard.Right>
                              </RowCard>
                            </NavLink>
                          );
                        })}
                      </div>
                      {/* Footer */}
                      <div className="p-3">
                        {dataTables?.meta !== undefined &&
                          dataTables?.links !== undefined && (
                            <TablePaginantion
                              totalPages={dataTables?.meta?.last_page || 0}
                              totalItems={dataTables?.meta?.total || 0}
                              pageSize={dataTables?.data.length || 0}
                              currentPage={dataTables?.meta?.current_page || 1}
                              hasNext={
                                (dataTables?.meta?.current_page || 0) <
                                (dataTables?.meta?.last_page || 0)
                              }
                              hasPrevious={
                                (dataTables?.meta?.current_page || 0) > 1
                              }
                              onPageChange={(page) => {
                                setPage(page);
                              }}
                              onNext={() => {
                                if (
                                  dataTables?.meta?.current_page &&
                                  dataTables?.meta?.last_page
                                ) {
                                  if (
                                    dataTables.meta?.current_page <
                                    dataTables.meta?.last_page
                                  ) {
                                    setPage(dataTables.meta?.current_page + 1);
                                  }
                                }
                              }}
                              onPrevious={() => {
                                if (
                                  dataTables?.meta?.current_page &&
                                  dataTables?.meta?.last_page
                                ) {
                                  if (dataTables.meta?.current_page > 1) {
                                    setPage(dataTables.meta?.current_page - 1);
                                  }
                                }
                              }}
                            />
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        <FunctionalCore />
      )}
    </>
  );
};

export default CentroDeDatos;
